import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BedDouble, Bath, Maximize, MapPin, Heart, Share2 } from "lucide-react";
import { aed, cap, waShareUrl, dl } from "@/lib/docstate";
import { useShortlist } from "@/context/ShortlistContext";

export const PropertyCard = ({ listing, index = 0 }) => {
  const cover = listing.cover_image || (listing.photos || [])[0];
  const ref = listing.reference || listing.id;
  const { has, toggle } = useShortlist();
  const saved = has(listing.id);
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.5, delay: Math.min(index * 0.06, 0.4), ease: [0.16, 1, 0.3, 1] }}
    >
      <Link
        to={dl(`/property/${ref}`)}
        data-testid={`property-card-${listing.id}`}
        className="group block overflow-hidden rounded-xl border border-[#C9A24B]/20 bg-[#1A191D] shadow-[0_10px_30px_rgba(0,0,0,0.5)] transition-all duration-500 hover:-translate-y-1.5 hover:border-[#C9A24B]/60 hover:shadow-[0_18px_45px_rgba(201,162,75,0.2)]"
      >
        <div className="relative aspect-[4/3] overflow-hidden">
          {cover ? (
            <img src={cover} alt={listing.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
          ) : (
            <div className="h-full w-full bg-[#121214]" />
          )}
          <div className="absolute inset-x-0 top-0 flex items-start justify-between p-3">
            <span className="rounded-full bg-[#0B0B0C]/80 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-[#F3E5AB] backdrop-blur-sm">
              For {cap(listing.purpose)}
            </span>
            {listing.completion_status === "off-plan" && (
              <span className="rounded-full bg-[#C9A24B] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-[#0B0B0C]">Off-Plan</span>
            )}
          </div>
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggle(listing); }}
            data-testid={`save-listing-${listing.id}`}
            aria-label={saved ? "Remove from shortlist" : "Save to shortlist"}
            className="absolute bottom-3 right-3 z-20 flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-[#0B0B0C]/70 backdrop-blur-sm transition-colors hover:border-[#C9A24B]"
          >
            <Heart className={`h-4 w-4 transition-colors ${saved ? "fill-[#C9A24B] text-[#C9A24B]" : "text-white"}`} />
          </button>
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(waShareUrl(listing), "_blank", "noopener,noreferrer"); }}
            data-testid={`share-listing-${listing.id}`}
            aria-label="Share this property on WhatsApp"
            className="absolute bottom-3 right-14 z-20 flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-[#0B0B0C]/70 backdrop-blur-sm transition-colors hover:border-[#C9A24B]"
          >
            <Share2 className="h-4 w-4 text-white" />
          </button>
          <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0B0B0C] via-[#0B0B0C]/40 to-transparent p-4">
            <p className="ds-display text-xl font-semibold text-[#F3E5AB]" data-testid={`property-card-price-${listing.id}`}>
              {aed(listing.price, listing.purpose === "rent" ? listing.price_period : "")}
            </p>
          </div>
        </div>
        <div className="p-4">
          <h3 className="ds-display truncate text-base font-semibold text-white">{listing.title}</h3>
          <p className="mt-1 flex items-center gap-1 text-xs text-[#9D8D83]">
            <MapPin className="h-3 w-3 text-[#C9A24B]" /> {listing.community}
          </p>
          <div className="mt-3 flex items-center gap-4 border-t border-white/5 pt-3 text-xs text-[#C9BBB2]">
            {listing.type === "studio" ? (
              <span className="inline-flex items-center gap-1.5"><BedDouble className="h-3.5 w-3.5 text-[#C9A24B]" /> Studio</span>
            ) : (
              <span className="inline-flex items-center gap-1.5"><BedDouble className="h-3.5 w-3.5 text-[#C9A24B]" /> {listing.bedrooms} Bed</span>
            )}
            <span className="inline-flex items-center gap-1.5"><Bath className="h-3.5 w-3.5 text-[#C9A24B]" /> {listing.bathrooms} Bath</span>
            {listing.area_sqft ? <span className="inline-flex items-center gap-1.5"><Maximize className="h-3.5 w-3.5 text-[#C9A24B]" /> {Number(listing.area_sqft).toLocaleString()} sqft</span> : null}
          </div>
        </div>
      </Link>
    </motion.div>
  );
};
